import apiClient, { retryRequest } from "./axios";

/**
 * 导出相关API
 */
export const exportApi = {
    /**
     * 导出单个资源到Excel
     * @param {string} resourceType - 资源类型，如schedule, flight等
     * @param {number|string} resourceId - 资源ID
     * @param {Object} options - 导出选项
     * @returns {Promise<Object>} 导出任务对象或下载链接
     */
    exportResource: async (resourceType, resourceId, options = {}) => {
        try {
            const response = await apiClient.post(`/v1/export/${resourceType}/${resourceId}`, options);
            return response;
        } catch (error) {
            console.error(`导出${resourceType}失败:`, error);
            throw error;
        }
    },

    /**
     * 批量导出资源到Excel
     * @param {string} resourceType - 资源类型，如schedule, flight等
     * @param {Array<number|string>} resourceIds - 资源ID数组
     * @param {Object} options - 导出选项
     * @returns {Promise<Object>} 导出任务对象或下载链接
     */
    batchExportResources: async (resourceType, resourceIds, options = {}) => {
        try {
            // 确保使用正确的API路径和参数格式
            const response = await apiClient.post(`/v1/export/${resourceType}/batch`, {
                resource_ids: resourceIds,
                options,
            });
            return response;
        } catch (error) {
            console.error(`批量导出${resourceType}失败:`, error);
            throw error;
        }
    },

    /**
     * 导出航班监控数据（根据筛选条件）
     * @param {Object} filterParams - 筛选参数
     * @param {string} exportMode - 导出模式：'standard' | 'settlement'（走 v1）| 'sales'（走 v3）
     * @returns {Promise<Object>} 导出任务对象或下载链接
     */
    exportFlightOperations: async (filterParams = {}, exportMode = "standard") => {
        try {
            // 销售导出已整体迁 v3（标准列 + 客户 + 订单号）：v1 导出路由已冻结，
            // 不再承接该模式，也不再在 v1 路由里加组织权限逻辑。
            // 两条入口分开调用，返回体都是 { download_url, file_name }，调用方无需分支。
            if (exportMode === "sales") {
                const { export_mode: _ignored, ...salesPayload } = filterParams;
                const response = await apiClient.post(
                    `/v3/settlement/flight-operations/export`,
                    salesPayload,
                );
                return response;
            }
            // 将 export_mode 添加到 filterParams 中
            const requestData = {
                ...filterParams,
                export_mode: exportMode,
            };
            const response = await apiClient.post(`/v1/export/flight_operation/filtered`, requestData);
            return response;
        } catch (error) {
            console.error("导出航班监控数据失败:", error);
            throw error;
        }
    },

    /**
     * 批量导出选中的航班监控数据
     * @param {Array<number>} flightIds - 航班ID数组
     * @param {Object} options - 导出选项
     * @returns {Promise<Object>} 导出任务对象或下载链接
     */
    batchExportFlightOperations: async (flightIds, options = {}) => {
        try {
            return await exportApi.batchExportResources("flight_operation", flightIds, options);
        } catch (error) {
            console.error("批量导出航班监控数据失败:", error);
            throw error;
        }
    },

    /**
     * 获取导出任务状态
     * @param {string} taskId - 任务ID
     * @returns {Promise<Object>} 任务状态信息
     */
    getExportTaskStatus: async (taskId) => {
        try {
            // 使用重试机制，因为导出可能需要一段时间
            return await retryRequest(
                () => apiClient.get(`/v1/export/tasks/${taskId}`),
                3, // 最大重试3次
                1000, // 初始延迟1秒
                (error) => !error.response || error.response.status >= 500,
            );
        } catch (error) {
            console.error("获取导出任务状态失败:", error);
            throw error;
        }
    },

    /**
     * 导出计划到Excel (使用通用导出资源API)
     * @param {number} scheduleId - 计划ID
     * @param {Object} options - 导出选项
     * @returns {Promise<Object>} 下载链接
     */
    exportSchedule: async (scheduleId, options = {}) => {
        try {
            // 使用通用的导出资源API路径，使用单数形式"schedule"
            return await exportApi.exportResource("schedule", scheduleId, options);
        } catch (error) {
            console.error("导出计划失败:", error);
            throw error;
        }
    },

    /**
     * 批量导出计划到Excel (使用通用批量导出资源API)
     * @param {Array<number>} scheduleIds - 计划ID数组
     * @param {Object} options - 导出选项
     * @returns {Promise<Object>} 下载链接
     */
    batchExportSchedules: async (scheduleIds, options = {}) => {
        try {
            // 直接调用通用资源批量导出方法，使用单数形式"schedule"
            return await exportApi.batchExportResources("schedule", scheduleIds, options);
        } catch (error) {
            console.error("批量导出计划失败:", error);
            throw error;
        }
    },

    /**
     * 根据筛选条件导出ULD数据
     * @param {Object} filters - 筛选条件
     * @param {Object} options - 导出选项
     * @returns {Promise<Object>} 导出结果
     */
    exportFilteredUlds: async (filters = {}, options = {}) => {
        try {
            const response = await apiClient.post(`/v1/export/uld/filtered`, {
                filters,
                options,
            });
            return response;
        } catch (error) {
            console.error("导出ULD数据失败:", error);
            throw error;
        }
    },

    /**
     * 导出ULD机场维度统计数据
     * @param {Object} filters - 筛选条件
     * @param {Object} options - 导出选项
     * @returns {Promise<Object>} 导出结果
     */
    exportUldAirportStatistics: async (filters = {}, options = {}) => {
        try {
            const response = await apiClient.post(`/v1/export/uld/airport-statistics`, {
                filters,
                options,
            });
            return response;
        } catch (error) {
            console.error("导出ULD机场维度统计数据失败:", error);
            throw error;
        }
    },

    /**
     * 导出ULD航班维度统计数据
     * @param {Object} filters - 筛选条件
     * @param {Object} options - 导出选项
     * @returns {Promise<Object>} 导出结果
     */
    exportUldFlightStatistics: async (filters = {}, options = {}) => {
        try {
            const response = await apiClient.post(`/v1/export/uld/flight-statistics`, {
                filters,
                options,
            });
            return response;
        } catch (error) {
            console.error("导出ULD航班维度统计数据失败:", error);
            throw error;
        }
    },

    /**
     * 导出长时间无动态ULD数据
     * @param {Object} filters - 筛选条件
     * @param {Object} options - 导出选项
     * @returns {Promise<Object>} 导出结果
     */
    exportInactiveUlds: async (filters = {}, options = {}) => {
        try {
            const response = await apiClient.post(`/v1/export/uld/inactive-ulds`, {
                filters,
                options,
            });
            return response;
        } catch (error) {
            console.error("导出长时间无动态ULD数据失败:", error);
            throw error;
        }
    },

    /**
     * 根据筛选条件导出主单号(MAWB)明细
     * @param {Object} filters - 筛选条件（stock_id / allocated_to_name / allocation_state / status / full_mawb / allocation_date）
     * @param {Object} options - 导出选项
     * @returns {Promise<Object>} 导出结果
     */
    exportMawbUsages: async (filters = {}, options = {}) => {
        try {
            const response = await apiClient.post(`/v1/export/mawb-usages/filtered`, {
                filters,
                options,
            });
            return response;
        } catch (error) {
            console.error("导出主单号明细失败:", error);
            throw error;
        }
    },
};

export default exportApi;
